import React, { useState, useEffect } from 'react';
import { doc, updateDoc, deleteDoc, arrayUnion, arrayRemove, getDocs, collection, getDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import './GroupSettings.css';

function GroupSettings({ group, user, userData, onClose, onGroupDeleted }) {
  const [users, setUsers] = useState([]);
  const [memberDetails, setMemberDetails] = useState({});
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isCreator = group.createdBy === user.uid;
  const isAdmin = userData?.role === 'admin';
  const canManage = isCreator || isAdmin; // Admins can manage any group


  // Fetch member details
  useEffect(() => {
    const fetchMemberDetails = async () => {
      const details = {};
      const nonExistentUsers = [];
      
      for (const memberId of group.members || []) {
        try {
          const userDoc = await getDoc(doc(db, 'users', memberId));
          if (userDoc.exists()) {
            details[memberId] = userDoc.data();
          } else {
            nonExistentUsers.push(memberId);
          }
        } catch (err) {
          console.error('Error fetching user:', err);
        }
      }
      
      setMemberDetails(details);
      
      // Auto-cleanup non-existent users if admin
      if (isAdmin && nonExistentUsers.length > 0) {
        console.log('Found non-existent users:', nonExistentUsers);
        cleanupNonExistentUsers(nonExistentUsers);
      }
    };

    fetchMemberDetails();
  }, [group.members, isAdmin]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const usersData = usersSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(u => !group.members?.includes(u.id));
        setUsers(usersData);
        setFilteredUsers(usersData);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };

    fetchUsers();
  }, [group.members]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredUsers(users);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = users.filter(u => 
        u.name?.toLowerCase().includes(query) || 
        u.email?.toLowerCase().includes(query)
      );
      setFilteredUsers(filtered);
    }
  }, [searchQuery, users]);

  const handleAddMember = async (userId) => {
    setLoading(true);
    setError('');
    try {
      const groupRef = doc(db, 'groups', group.id);
      await updateDoc(groupRef, {
        members: arrayUnion(userId)
      });
      
      setUsers(users.filter(u => u.id !== userId));
      setFilteredUsers(filteredUsers.filter(u => u.id !== userId));
    } catch (err) {
      setError('Failed to add member');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    setLoading(true);
    setError('');
    try {
      const groupRef = doc(db, 'groups', group.id);
      await updateDoc(groupRef, {
        members: arrayRemove(userId),
        admins: arrayRemove(userId)
      });
    } catch (err) {
      setError('Failed to remove member');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const cleanupNonExistentUsers = async (userIds) => {
    try {
      const groupRef = doc(db, 'groups', group.id);
      
      // Remove all non-existent users at once
      for (const userId of userIds) {
        await updateDoc(groupRef, {
          members: arrayRemove(userId),
          admins: arrayRemove(userId)
        });
      }
      
      console.log(`✅ Cleaned up ${userIds.length} non-existent user(s) from group ${group.name}`);
    } catch (err) {
      console.error('Error cleaning up non-existent users:', err);
    }
  };

  const handleDeleteGroup = async () => {
    setLoading(true);
    setError('');
    try {
      // 1. Delete all messages in this group
      const messagesQuery = query(
        collection(db, 'messages'),
        where('chatId', '==', group.id)
      );
      const messagesSnapshot = await getDocs(messagesQuery);
      
      for (const messageDoc of messagesSnapshot.docs) {
        await deleteDoc(doc(db, 'messages', messageDoc.id));
      }

      // 2. Delete the group
      await deleteDoc(doc(db, 'groups', group.id));
      
      onGroupDeleted();
      onClose();
    } catch (err) {
      setError('Failed to delete group: ' + err.message);
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <div className="group-settings-overlay" onClick={onClose}>
      <div className="group-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Group Settings</h2>
          <button onClick={onClose} className="close-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="settings-content">
          <div className="group-info-section">
            <h3>{group.name}</h3>
            <p>{group.members?.length || 0} members</p>
          </div>

          {canManage ? (
            <>
              <div className="settings-section">
                <h4>Members</h4>
                <div className="members-list">
                  {group.members?.map((memberId) => {
                    const memberExists = memberDetails[memberId];
                    return (
                      <div key={memberId} className={`member-item ${!memberExists ? 'non-existent' : ''}`}>
                        <div className="member-info">
                          <span className="member-name">
                            {memberExists ? memberDetails[memberId]?.name : 'User not found'}
                          </span>
                          {!memberExists && (
                            <span className="deleted-badge">Deleted</span>
                          )}
                          {memberId === group.createdBy && memberExists && (
                            <span className="creator-badge">Creator</span>
                          )}
                        </div>
                        {memberId !== group.createdBy && (
                          <button
                            onClick={() => handleRemoveMember(memberId)}
                            className="remove-member-btn"
                            disabled={loading}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="settings-section">
                <h4>Add Members</h4>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or email..."
                  className="search-input"
                />
                <div className="add-members-list">
                  {filteredUsers.length === 0 ? (
                    <p className="no-users">No users available to add</p>
                  ) : (
                    filteredUsers.map((u) => (
                      <div key={u.id} className="add-member-item">
                        <div className="add-member-info">
                          <span className="user-name">{u.name || 'User'}</span>
                          {searchQuery && (
                            <span className="user-email-hint">{u.email}</span>
                          )}
                        </div>
                        <button
                          onClick={() => handleAddMember(u.id)}
                          className="add-member-btn"
                          disabled={loading}
                        >
                          Add
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="danger-zone">
                <h4>Danger Zone</h4>
                {!showDeleteConfirm ? (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="delete-group-btn"
                  >
                    Delete Group
                  </button>
                ) : (
                  <div className="delete-confirm">
                    <p>Are you sure? This will delete the group and all its messages. This cannot be undone.</p>
                    <div className="confirm-actions">
                      <button
                        onClick={handleDeleteGroup}
                        className="confirm-delete-btn"
                        disabled={loading}
                      >
                        {loading ? 'Deleting...' : 'Yes, Delete Everything'}
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(false)}
                        className="cancel-delete-btn"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="not-creator-message">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <p>Only the group creator can manage settings.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default GroupSettings;
